// GCCV2DobForm.tsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Calendar, ArrowRight } from "lucide-react";
import {
  getStoredAstriaGCCV2Dob,
  setStoredAstriaGCCV2Dob,
} from "../../../utils/GCC/astriaGCCV2Dob";
import {
  getStoredGCCV2Language,
  setStoredGCCV2Language,
  GCCV2Language,
} from "../../../utils/GCC/astriaGCCV2Language";
import { SHELL_COLORS_GCC_V2, getGCCV2Fonts } from "./AstriaGCCV2Shell";
import { useAuth } from "../../../contexts/AuthContext";
import { api } from "../../../services/api";
import {
  GCC_V2_TONE_MODE_OPTIONS,
  DEFAULT_GCC_V2_TONE_MODE,
  GCCV2ToneMode,
} from "../../../utils/GCC/astriaGCCV2ToneMode";
import { clearAstriaGCCV2ResponseCache } from "../../../utils/GCC/astriaGCCV2ResponseCache";

// ─── Astria GCC v2 — one-time DOB gate. Collected once, shared across all

const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) formatted += "/";
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > new Date().getFullYear()) year = new Date().getFullYear();
    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  }
  return value;
};

const CONTENT: Record<
  GCCV2Language,
  {
    brand: string;
    title: string;
    subtext: string;
    languageLabel: string;
    dobLabel: string;
    dobPlaceholder: string;
    dobRequiredError: string;
    toneLabel: string;
    toneHint: string;
    submitButton: string;
    hint: string;
    footer: string;
  }
> = {
  en: {
    brand: "Astria GCC",
    title: "A quiet space that understands you.",
    subtext: "Share your date of birth to begin.",
    languageLabel: "Language",
    dobLabel: "Date of Birth",
    dobPlaceholder: "DD / MM / YYYY",
    dobRequiredError: "Please enter a valid date of birth.",
    toneLabel: "Tone",
    toneHint: "How your companion speaks with you in Arabic.",
    submitButton: "Begin",
    hint: "Used only to sense your personal rhythm. Never shared, never shown as a prediction.",
    footer: "Your companion for quiet reflection.",
  },
  ar: {
    brand: "أستريا الخليج",
    title: "مساحة هادئة تفهمك.",
    subtext: "شاركنا تاريخ ميلادك للبدء.",
    languageLabel: "اللغة",
    dobLabel: "تاريخ الميلاد",
    dobPlaceholder: "يوم / شهر / سنة",
    dobRequiredError: "يرجى إدخال تاريخ ميلاد صحيح.",
    toneLabel: "اللهجة",
    toneHint: "الطريقة التي يتحدث بها رفيقك معك باللغة العربية.",
    submitButton: "ابدأ",
    hint: "يُستخدم فقط لفهم إيقاعك الشخصي. لا تتم مشاركته أو عرضه كتنبؤ.",
    footer: "رفيقك للتأمل الهادئ.",
  },
};

const GCCV2DobForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateCurrentUser } = useAuth();
  const initialLanguage = getStoredGCCV2Language();

  const [dob, setDob] = useState(getStoredAstriaGCCV2Dob() || "");
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<GCCV2Language>(initialLanguage);
  const [toneMode, setToneMode] = useState<GCCV2ToneMode>(
    user?.gccToneMode || DEFAULT_GCC_V2_TONE_MODE,
  );

  const fonts = getGCCV2Fonts(language);
  const dir = language === "ar" ? "rtl" : "ltr";
  const t = CONTENT[language];

  const handleSubmit = async () => {
    if (!dob || dob.split("/").length !== 3) {
      setError(t.dobRequiredError);
      return;
    }

    const previousLanguage = getStoredGCCV2Language();
    const previousToneMode = user?.gccToneMode || DEFAULT_GCC_V2_TONE_MODE;
    const languageChanged = language !== previousLanguage;
    const toneChanged = toneMode !== previousToneMode;

    setStoredAstriaGCCV2Dob(dob);
    setStoredGCCV2Language(language);

    // Save the dialect choice up front (PUT /backend/user/:id, same as the
    // account-level persistence pattern used elsewhere) so the very first
    // reading already uses the right tone — best-effort: a failed save
    // shouldn't block the user from continuing.
    if (user?._id && toneChanged) {
      try {
        await api.put(
          `/backend/user/${user._id}`,
          { gccToneMode: toneMode },
          { skipAuthRedirect: true },
        );
        updateCurrentUser({ gccToneMode: toneMode });
      } catch {
        // ignore — backend still defaults to "gulf" if unset, and a stale
        // token here must never hard-redirect and abandon this submit
      }
    }

    // Language/tone changed since last visit → any cached tab response was
    // generated under the old settings and would otherwise show stale
    // content until manually refreshed. Clear so every tab regenerates.
    if (languageChanged || toneChanged) {
      clearAstriaGCCV2ResponseCache();
    }

    const next = searchParams.get("next");
    navigate(
      next && next.startsWith("/astria-gcc-v2")
        ? next
        : "/astria-gcc-v2/timing",
    );
  };

  return (
    <div
      dir={dir}
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: SHELL_COLORS_GCC_V2.bg, fontFamily: fonts.body }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full"
        style={{
          background: SHELL_COLORS_GCC_V2.card,
          borderRadius: 20,
          boxShadow:
            "0 12px 40px rgba(43,39,37,0.08), 0 2px 8px rgba(43,39,37,0.03)",
          maxWidth: 480,
          padding: 36,
          border: `1px solid ${SHELL_COLORS_GCC_V2.cardBorder}`,
        }}
      >
        {/* ─── Brand ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-4">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${SHELL_COLORS_GCC_V2.accent} 0%, #E3C77A 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 16px ${SHELL_COLORS_GCC_V2.accent}44`,
            }}
          >
            <Sparkles className="w-5 h-5" style={{ color: "#FFFFFF" }} />
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: SHELL_COLORS_GCC_V2.mutedColor,
              letterSpacing: "0.1em",
            }}
          >
            {t.brand}
          </div>
        </div>

        {/* ─── Title ────────────────────────────────────────────────────── */}
        <div
          style={{
            fontSize: 24,
            fontWeight: 600,
            color: SHELL_COLORS_GCC_V2.titleColor,
            marginBottom: 4,
            lineHeight: 1.3,
            fontFamily: fonts.heading,
          }}
        >
          {t.title}
        </div>
        <p
          style={{
            fontSize: 14,
            color: SHELL_COLORS_GCC_V2.mutedColor,
            marginBottom: 28,
            lineHeight: 1.7,
          }}
        >
          {t.subtext}
        </p>

        {/* ─── Form Section ────────────────────────────────────────────── */}
        <div
          style={{
            background: SHELL_COLORS_GCC_V2.section,
            borderRadius: 16,
            padding: 24,
          }}
        >
          {/* ─── Language Selection ─────────────────────────────────────── */}
          <div style={{ marginBottom: 20 }}>
            <label
              className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-1.5"
              style={{ color: SHELL_COLORS_GCC_V2.mutedColor }}
            >
              {t.languageLabel}
            </label>
            <div
              className="inline-flex items-center"
              style={{
                background: SHELL_COLORS_GCC_V2.white,
                borderRadius: 999,
                padding: 3,
                border: `1px solid ${SHELL_COLORS_GCC_V2.cardBorder}`,
              }}
            >
              {(["en", "ar"] as GCCV2Language[]).map((lang) => {
                const isActive = language === lang;
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    className="rounded-full text-xs font-medium transition-all duration-200"
                    style={{
                      paddingInlineStart: 16,
                      paddingInlineEnd: 16,
                      paddingTop: 7,
                      paddingBottom: 7,
                      background: isActive
                        ? SHELL_COLORS_GCC_V2.accent
                        : "transparent",
                      color: isActive
                        ? SHELL_COLORS_GCC_V2.white
                        : SHELL_COLORS_GCC_V2.mutedColor,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    {lang === "en" ? "EN" : "AR"}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label
              className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-1.5"
              style={{ color: SHELL_COLORS_GCC_V2.mutedColor }}
            >
              {t.dobLabel}
              <span
                className="ms-1"
                style={{ color: SHELL_COLORS_GCC_V2.accent }}
              >
                *
              </span>
            </label>
            <div className="relative">
              <Calendar
                className="absolute w-4 h-4"
                style={{
                  color: SHELL_COLORS_GCC_V2.mutedColor,
                  insetInlineStart: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                type="text"
                inputMode="numeric"
                placeholder={t.dobPlaceholder}
                value={dob}
                onChange={(e) => setDob(formatDateValue(e.target.value))}
                onBlur={(e) => {
                  setDob(validateDateValue(e.target.value));
                  e.currentTarget.style.borderColor =
                    SHELL_COLORS_GCC_V2.cardBorder;
                  e.currentTarget.style.boxShadow = "none";
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor =
                    SHELL_COLORS_GCC_V2.accent;
                  e.currentTarget.style.boxShadow =
                    SHELL_COLORS_GCC_V2.accentGlow;
                }}
                className="w-full rounded-full border py-3 text-[15px] shadow-sm focus:outline-none transition-all duration-200"
                style={{
                  borderColor: SHELL_COLORS_GCC_V2.cardBorder,
                  color: SHELL_COLORS_GCC_V2.titleColor,
                  background: SHELL_COLORS_GCC_V2.white,
                  fontFamily: fonts.body,
                  paddingInlineStart: 44,
                  paddingInlineEnd: 16,
                }}
              />
            </div>
            <p
              className="mt-2 text-xs"
              style={{ color: SHELL_COLORS_GCC_V2.mutedColor, opacity: 0.85 }}
            >
              {t.hint}
            </p>
          </div>

          {/* ─── Tone Selection — Arabic only, since dialect only shapes
               how Arabic replies sound (formal MSA vs. Gulf vs. Kuwaiti) ── */}
          {language === "ar" && (
            <div style={{ marginTop: 20 }}>
              <label
                className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-1.5"
                style={{ color: SHELL_COLORS_GCC_V2.mutedColor }}
              >
                {t.toneLabel}
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {GCC_V2_TONE_MODE_OPTIONS.map((opt) => {
                  const isActive = toneMode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setToneMode(opt.value)}
                      className="rounded-full text-xs font-medium border transition-all duration-200"
                      style={{
                        paddingInlineStart: 14,
                        paddingInlineEnd: 14,
                        paddingTop: 7,
                        paddingBottom: 7,
                        borderColor: isActive
                          ? SHELL_COLORS_GCC_V2.accent
                          : SHELL_COLORS_GCC_V2.cardBorder,
                        background: isActive
                          ? SHELL_COLORS_GCC_V2.accentSoft
                          : SHELL_COLORS_GCC_V2.white,
                        color: isActive
                          ? SHELL_COLORS_GCC_V2.accent
                          : SHELL_COLORS_GCC_V2.mutedColor,
                        fontWeight: isActive ? 600 : 500,
                      }}
                    >
                      {opt.label[language]}
                    </button>
                  );
                })}
              </div>
              <p
                className="mt-2 text-xs"
                style={{
                  color: SHELL_COLORS_GCC_V2.mutedColor,
                  opacity: 0.85,
                }}
              >
                {t.toneHint}
              </p>
            </div>
          )}
        </div>

        {/* ─── Error Message ───────────────────────────────────────────── */}
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-sm flex items-center gap-2"
            style={{ color: "#B4574A" }}
          >
            <span>✧</span>
            {error}
          </motion.p>
        )}

        {/* ─── Submit Button ───────────────────────────────────────────── */}
        <motion.button
          onClick={handleSubmit}
          className="mt-6 w-full rounded-full py-3.5 text-sm font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 group"
          style={{
            background: `linear-gradient(135deg, ${SHELL_COLORS_GCC_V2.accent} 0%, #E3C77A 100%)`,
            boxShadow: `0 4px 16px ${SHELL_COLORS_GCC_V2.accent}44`,
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {t.submitButton}
          <ArrowRight
            className="w-4 h-4 group-hover:translate-x-1 transition-transform"
            style={{ transform: dir === "rtl" ? "scaleX(-1)" : undefined }}
          />
        </motion.button>

        {/* ─── Footer ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{
            textAlign: "center",
            marginTop: 24,
            paddingTop: 20,
            borderTop: `1px solid ${SHELL_COLORS_GCC_V2.cardBorder}`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: SHELL_COLORS_GCC_V2.mutedColor,
              opacity: 0.8,
              letterSpacing: "0.05em",
            }}
          >
            {t.footer}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default GCCV2DobForm;
